/*
 * Ryan O'Dowd
 * 2024-03-01
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  createAssignment,
  deleteAssignment,
  updateAssignment,
} from '@utils/store/actions';
import AssignmentSettings from '@classes/AssignmentSettings';
import Banner from '@components/common/Banner';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Checkbox from '@components/common/Checkbox';
import DateRange from '../DateRange';
import Dialog from '@components/common/Dialog';
import FormControl from '@components/common/FormControl';
import Passage from '@components/Passage';
import PassageSelection from '@components/PassageSelection';
import Select from '@components/common/Select';
import TextInput from '@components/common/TextInput';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';

const EditAssignmentDialog = (props: EditAssignmentDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_assignmentName, setAssignmentName] = useState(props.isNew ? '' : (props.assignment.name || ''));
  const [_dateAvailable, setDateAvailable] = useState(props.assignment?.date_available ? new Date(props.assignment.date_available) : null);
  const [_dateDeadline, setDateDeadline] = useState(props.assignment?.date_deadline ? new Date(props.assignment.date_deadline) : null);
  // @TODO: const [_status, setStatus] = useState(props.assignment?.status || 'draft');
  const [_type, setType] = useState(props.assignment?.assignment_type || '');
  const [_pipeDelimitedReadingsText, setPipeDelimitedReadingsText] = useState('');
  const [_pipeDelimitedVerseMemoriesText, setPipeDelimitedVerseMemoriesText] = useState('');

  const minigames = hooks.useTypedSelector((state) => state.minigames);
  const initialPassage = props.assignment?.assignment_object?.passage;
  const [_passage, setPassage] = useState(initialPassage ? CPassage.createFromObject(initialPassage) : null);
  const [_verseMemoryNumLessonsToComplete, setVerseMemoryNumLessonsToComplete] = useState(props.assignment?.assignment_object?.num_lessons_to_complete ?? 3);
  const [_isPassageSelectionOpen, setIsPassageSelectionOpen] = useState(false);

  const preferredTranslation = hooks.usePreferredTranslation();

  const _getInitialGameTypesEnabled = () => {
    const initialEnabled = {};
    Object.values(minigames).forEach((m: any) => {
      initialEnabled[m.id] = props.assignment?.assignment_object?.game_types_enabled?.includes(m.id) || false;
    });

    return initialEnabled;
  };
  const [_noAssignmentName, setNoAssignmentName] = useState((['verse_memory', 'reading'].includes(props.assignment?.assignment_type) && (!props.assignment?.name || props.isNew)));
  const [_verseMemoryGameTypesEnabled, setVerseMemoryGameTypesEnabled] = useState(_getInitialGameTypesEnabled());
  const [_verseMemoryGameDifficulty, setVerseMemoryGameDifficulty] = useState(props.assignment?.assignment_object?.game_difficulty || 'choice');
  const [_verseMemoryMustMemorize, setVerseMemoryMustMemorize] = useState(props.assignment?.assignment_object?.must_memorize || 0);
  const [_lectureText, setLectureText] = useState(props.assignment?.assignment_object?.text || '');

  const [_validationError, setValidationError] = useState('');

  const isAdmin = hooks.useIsAdmin();

  const getMinDateAvailable = () => { // @TODO: abstract with EditModuleDialog
    const goalDateAvailable = props.goal.date_available ? new Date(props.goal.date_available) : null;
    const moduleDateAvailable = props.module.date_available ? new Date(props.module.date_available) : null;
    // @TODO: disallow picking a date before this.date_deadline (in the case of picking deadline and then availablity)

    return new Date(Math.max(...[goalDateAvailable?.getTime(), moduleDateAvailable?.getTime()].filter(Boolean)));
  };

  const getMaxDateAvailable = () => { // @TODO: abstract with EditModuleDialog
    const assignmentDateDeadline = _dateDeadline;
    const goalDateDeadline = props.goal.date_deadline ? new Date(props.goal.date_deadline) : null;
    const moduleDateDeadline = props.module.date_deadline ? new Date(props.module.date_deadline) : null;
    // @TODO: disallow picking a date before this.date_deadline (in the case of picking deadline and then availablity)

    return new Date(Math.min(...[goalDateDeadline?.getTime(), moduleDateDeadline?.getTime(), assignmentDateDeadline?.getTime()].filter(Boolean)));
  };

  const getMinDateDeadline = () => { // @TODO: abstract with EditModuleDialog
    const assignmentDateAvailable = _dateAvailable || new Date(0);
    const goalDateAvailable = props.goal.date_available ? new Date(props.goal.date_available) : null;
    const moduleDateAvailable = props.module.date_available ? new Date(props.module.date_available) : null;

    return new Date(Math.max(...[goalDateAvailable?.getTime(), moduleDateAvailable?.getTime(), assignmentDateAvailable?.getTime()].filter(Boolean)));
  };

  const getMaxDateDeadline = () => { // @TODO: abstract with EditModuleDialog
    const goalDateDeadline = props.goal.date_deadline ? new Date(props.goal.date_deadline) : null;
    const moduleDateDeadline = props.module.date_deadline ? new Date(props.module.date_deadline) : null;

    return new Date(Math.min(...[goalDateDeadline?.getTime(), moduleDateDeadline?.getTime()].filter(Boolean)));
  };

  const _getDescriptionText = (type) => { // @TODO: maybe don't need this?
    if (type === 'verse_memory') {
      return 'Students must complete lessons and/or memorize the selected verses.'; // @TODO: update
    } else if (type === 'reading') {
      return 'Select verses for students to read.'; // @TODO: update
    } else if (type === 'lecture') {
      return 'Upload custom curriculum text for students to read.'; // @TODO: update
    }
  };

  return (
    <React.Fragment>
      <Dialog
        visible={!_isPassageSelectionOpen}
        onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
      >
        <Dialog.Title>
          {props.isNew ? 'Create new assignment' : 'Edit assignment'}
        </Dialog.Title>{/* @TODO: i18n */}
        <Dialog.Content>
          {!!_type &&
            <Banner
              type='info'
              text={_getDescriptionText(_type)}
            />
          }

          {props.isNew &&
            <Select
              label='Assignment type'// @TODO: i18n
              options={[
                {
                  label: 'Verse Memory',
                  value: 'verse_memory',
                },
                {
                  label: 'Reading',
                  value: 'reading',
                },
                {
                  label: 'Lecture',
                  value: 'lecture',
                },
              ]}
              value={_type}
              onChange={(value) => setType(value)}
            />
          }

          {!!_type &&
            <React.Fragment>
              {(!['verse_memory', 'reading'].includes(_type) || !_noAssignmentName) &&
                <TextInput
                  value={_assignmentName}
                  mode='outlined'
                  onChangeText={(text) => setAssignmentName(text)}
                  placeholder='e.g. "The birth of Christ"'
                  label='Assignment name'
                />
              }
              {['verse_memory', 'reading'].includes(_type) &&
                <Checkbox// @TODO: this has negative marginTop?
                  isChecked={!!_noAssignmentName}
                  onPress={() => {
                    setAssignmentName((curr) => {
                      if (curr || props.isNew) {
                        return '';
                      }

                      return props.assignment.name;
                    });
                    setNoAssignmentName((curr) => !curr);
                  }}
                  label='Match the passage reference (e.g. "Genesis 1:1")'
                />
              }

              <DateRange// @TODO: format this differently
                dateAvailable={_dateAvailable}
                dateDeadline={_dateDeadline}
                setDateAvailable={(newValue) => setDateAvailable(newValue)}
                setDateDeadline={(newValue) => setDateDeadline(newValue)}
                minDateAvailable={getMinDateAvailable()}
                maxDateAvailable={getMaxDateAvailable()}
                minDateDeadline={getMinDateDeadline()}
                maxDateDeadline={getMaxDateDeadline()}
              />

              {/* @TODO:
              <Select
                label='Assignment status'// @TODO: i18n
                options={[
                  {
                    label: 'Draft', // @TODO: only an option if new goal?
                    value: 'draft',
                  },
                  {
                    label: 'Published',
                    value: 'published',
                  },
                ]}
                value={_status}
                onChange={(value) => setStatus(value)}
              />
              */}
              {_type === 'verse_memory' &&
                <React.Fragment>
                  <FormControl
                    addPadding={true}
                    style={styles.memorizationSettings}// @TODO: should this be default for FormControls?
                  >
                    <FormControl
                      addPadding={true}
                      label='Memorization'
                    >
                      <Checkbox
                        isChecked={!!_verseMemoryMustMemorize}
                        onPress={() => setVerseMemoryMustMemorize((curr) => !curr)}
                        label='Require memorization'
                      />
                    </FormControl>

                    {!_verseMemoryMustMemorize &&
                      <React.Fragment>
                        <FormControl.Row>
                          <TextInput
                            value={_verseMemoryNumLessonsToComplete}
                            mode='outlined'
                            onChangeText={(text) => {
                              const newValue = text.replace(/[^0-9]/g, '');
                              setVerseMemoryNumLessonsToComplete(newValue);
                              if (!newValue || parseInt(newValue) === 0) {
                                setVerseMemoryGameTypesEnabled(_getInitialGameTypesEnabled());
                                setVerseMemoryGameDifficulty('choice');
                              }
                            }}
                            label='Number of lessons'
                          />{/* @TODO: tooltip helper */}

                          <Select
                            label='Lesson difficulty'// @TODO: i18n
                            options={[
                              {
                                label: 'Student choice',
                                value: 'choice',
                              },
                              {
                                label: 'Easy',
                                value: 'easy',
                              },
                              {
                                label: 'Medium',
                                value: 'medium',
                              },
                              {
                                label: 'Hard',
                                value: 'hard',
                              },
                            ]}
                            disabled={_verseMemoryNumLessonsToComplete === 0}
                            value={_verseMemoryGameDifficulty || ''}
                            onChange={(value) => setVerseMemoryGameDifficulty(value)}
                          />
                        </FormControl.Row>

                        <FormControl
                          addPadding={true}
                          label='Available games'// @TODO: i18n
                        >
                          <View style={styles.minigamesWrapper}>
                            {Object.values(minigames).map((minigame: any) => {
                              return (
                                <Checkbox
                                  key={minigame.id}
                                  disabled={_verseMemoryNumLessonsToComplete === 0}
                                  isChecked={_verseMemoryGameTypesEnabled[minigame.id]}
                                  onPress={() => setVerseMemoryGameTypesEnabled((curr) => ({..._verseMemoryGameTypesEnabled, [minigame.id]: !curr[minigame.id]}))}
                                  label={minigame.name}
                                />
                              );
                            })}
                          </View>
                        </FormControl>
                      </React.Fragment>
                    }
                  </FormControl>

                  <FormControl
                    addPadding={true}
                    label='Passage'// @TODO: i18n
                    style={styles.passageSelectionWrapper}
                  >
                    {/* @TODO: setting to require specific translation...add this to PassageSelection */}
                    {_passage &&
                      <Passage// @TODO: combine with Reading and VerseMemory
                        passage={_passage}
                        hideActions={true}
                        hideSelectionMenu={true}
                        hideWrinkleBadges={true}
                        hideProgressStyling={true}
                        showHeadings={true}
                        isExpanded={false}
                        isPressDisabled={true}
                      />
                    }
                    {(props.goal.classroom_goal_metadata.status === 'draft' || props.isNew) &&
                      <View style={styles.selectButtonWrapper}>
                        <Button onPress={() => setIsPassageSelectionOpen(true)}>
                          Select passage{/* @TODO: show currently selected passage as well */}{/* @TODO: i18n */}
                        </Button>
                      </View>
                    }
                  </FormControl>
                </React.Fragment>
              }
              {_type === 'reading' &&
                <FormControl
                  addPadding={true}
                  label='Passage'// @TODO: i18n
                  style={styles.passageSelectionWrapper}
                >
                  {_passage &&
                    <Passage// @TODO: combine with Reading and VerseMemory
                      passage={_passage}
                      hideActions={true}
                      hideSelectionMenu={true}
                      hideWrinkleBadges={true}
                      hideProgressStyling={true}
                      showHeadings={true}
                      isExpanded={false}
                      isPressDisabled={true}
                    />
                  }
                  {(props.goal.classroom_goal_metadata.status === 'draft' || props.isNew) &&
                    <View style={styles.selectButtonWrapper}>
                      <Button onPress={() => setIsPassageSelectionOpen(true)}>
                        Select passage{/* @TODO: show currently selected passage as well */}{/* @TODO: i18n */}
                      </Button>
                    </View>
                  }
                </FormControl>
              }
              {_type === 'lecture' &&
                <TextInput
                  value={_lectureText}
                  mode='outlined'
                  onChangeText={(text) => setLectureText(text)}
                  multiline={true}
                  numberOfLines={8}
                  label='Lecture text'// @TODO: better placeholder
                />
              }
              {/* @TODO:
              <p>TODO: move assignment to a different module</p>
              */}
            </React.Fragment>
          }

          {(isAdmin && props.isNew) &&
            <FormControl
              addPadding={true}
              label='Admin options'
              style={styles.adminSettingsWrapper}
            >
              <View style={styles.adminOptionsInner}>
                <TextInput
                  value={_pipeDelimitedReadingsText}
                  mode='outlined'
                  onChangeText={(text) => setPipeDelimitedReadingsText(text)}
                  label='Pipe-delimited READING passages'
                  placeholder={`e.g. "Passage 1|Passage 2|Passage 3"`}
                />

                <TextInput
                  value={_pipeDelimitedVerseMemoriesText}
                  mode='outlined'
                  onChangeText={(text) => setPipeDelimitedVerseMemoriesText(text)}
                  label='Pipe-delimited VERSE MEMORY passages'
                  placeholder={`e.g. "Passage 1|Passage 2|Passage 3"`}
                />

                <Button
                  onPress={async () => {
                    const sleep = (ms) => {
                      return new Promise((resolve) => setTimeout(resolve, ms));
                    };

                    const readingPassages = _pipeDelimitedReadingsText.split('|').filter((readingPassage) => readingPassage.length > 0);
                    for (const passageReference of readingPassages) {
                      const passage = CPassage.createPassageFromReference(passageReference, preferredTranslation.id);
                      let assignmentSettings = null;
                      try {
                        assignmentSettings = new AssignmentSettings(
                          false, // @NOTE: isAdmin is false because true would skip certain validations
                          'reading',
                          passage.prettyPassageName(),
                          true, // no assignment name
                          passage,
                          _verseMemoryNumLessonsToComplete,
                          _verseMemoryGameTypesEnabled,
                          _verseMemoryGameDifficulty,
                          _verseMemoryMustMemorize,
                          _lectureText,
                        ).validateAndMarshal();
                      } catch (e) {
                        setValidationError(e.message);
                        return;
                      }
                      dispatch(createAssignment(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.module.id, _assignmentName, _dateAvailable, _dateDeadline, 'published', 'reading', assignmentSettings));
                      await sleep(100); // @TODO: shouldn't need this // @TODO: this still isn't long enough for some of the race conditions
                    }

                    const verseMemoriesPassages = _pipeDelimitedVerseMemoriesText.split('|').filter((verseMemoryPassage) => verseMemoryPassage.length > 0);
                    for (const passageReference of verseMemoriesPassages) {
                      const passage = CPassage.createPassageFromReference(passageReference, preferredTranslation.id);
                      let assignmentSettings = null;
                      try {
                        assignmentSettings = new AssignmentSettings(
                          false, // @NOTE: isAdmin is false because true would skip certain validations
                          'verse_memory',
                          passage.prettyPassageName(),
                          true, // no assignment name
                          passage,
                          _verseMemoryNumLessonsToComplete,
                          _verseMemoryGameTypesEnabled,
                          _verseMemoryGameDifficulty,
                          true, // must memorize
                          _lectureText,
                        ).validateAndMarshal();
                      } catch (e) {
                        setValidationError(e.message);
                        return;
                      }
                      dispatch(createAssignment(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.module.id, _assignmentName, _dateAvailable, _dateDeadline, 'published', 'verse_memory', assignmentSettings));
                      await sleep(100); // @TODO: shouldn't need this
                    }

                    // @TODO: show success/failure (and clear text input on success)
                  }}
                >
                  Save
                </Button>
              </View>
            </FormControl>
          }

          <p style={styles.errorText}>{_validationError}</p>{/* @TODO: needs to be here after the switch to paper...put back in actions (allow in the Dialog component...like add an "error message" prop) */}
        </Dialog.Content>
        <Dialog.Actions>
          {!props.isNew && props.goal.classroom_goal_metadata.status === 'draft' &&
            <Button
              theme={styles.colors.alert}
              mode='text'
              onPress={() => {
                // @TODO: confirm with alert after letting teacher know how permanent this is...show how many kids have started (maybe not an option if progress has been made)
                if (window.confirm('Are you sure you want to delete this assignment?')) { // @TODO: replace with prettier confirm
                  dispatch(deleteAssignment(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.module.id, props.assignment.id));
                }
                props.onClose();
              }}
            >
              {i18n('delete')}
            </Button>
          }
          <Button
            mode='text'
            onPress={() => props.onClose()}
          >
            {i18n('cancel')}
          </Button>
          {/* @TODO: button to duplicate assignment */}
          <Button
            onPress={() => {
              let assignmentSettings = null;
              try {
                assignmentSettings = new AssignmentSettings(
                  isAdmin,
                  _type,
                  _assignmentName,
                  ['verse_memory', 'reading'].includes(_type) && _noAssignmentName,
                  _passage,
                  _verseMemoryNumLessonsToComplete,
                  _verseMemoryGameTypesEnabled,
                  _verseMemoryGameDifficulty,
                  _verseMemoryMustMemorize,
                  _lectureText,
                ).validateAndMarshal();
              } catch (e) {
                setValidationError(e.message);
                return;
              }
              if (props.isNew) {
                // @TODO: make sure all required fields are filled
                dispatch(createAssignment(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.module.id, _assignmentName, _dateAvailable, _dateDeadline, 'published', _type, assignmentSettings));
              } else {
                dispatch(updateAssignment(props.goal.classroom_goal_metadata.classroom_id, props.goal.id, props.assignment.id, _assignmentName, _dateAvailable, _dateDeadline, 'published', _type, assignmentSettings)); // @TODO: this should be only fields that have been updated (compare state variables with props.goal)
              }
              props.onClose(); // @TODO: wait for back-end call to complete?
            }}
          >
            {i18n('save')}
          </Button>
        </Dialog.Actions>
      </Dialog>
      {_isPassageSelectionOpen &&
        <PassageSelection
          onClose={() => setIsPassageSelectionOpen(false)}
          onSelectPassage={(newPassageJson) =>  {
            const newPassage = CPassage.createFromObject(newPassageJson);
            setPassage(newPassage);
            setIsPassageSelectionOpen(false);
          }}
        />
      }
    </React.Fragment>
  );
};

class EditAssignmentDialogProps {
  onClose: () => void;
  module: any;
  goal: any;

  assignment?: any;
  isNew?: boolean;
};

export default EditAssignmentDialog;
